"use client";

import { useEffect, useRef, useState } from "react";
import { Loader } from "lucide-react";
import { StudyWorkspace } from "./Workspace";
import {
  getStudyDocumentsLimits,
  getStudyNotebooks,
  Notebook,
  postStudyNotebooks,
} from "../client";
import { useCourses } from "@/lib/courses";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { client } from "../client/client.gen";
import { CLOUDFLARE_URL } from "@/lib/config";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { CanvasCourse } from "@/lib/types";
import { CreateFlowStep, NotebookProvider } from "../hooks/useNotebook";
import { WorkspaceSkeleton } from "./layout/workspace-skeleton";
import { StudyLandingPage } from "./Landing";
import {
  FinalAssignment,
  NotebookExtended,
  StudyNotUploadedDocument,
  StudyUserLimits,
} from "../types";
import { UserLimitsProvider } from "../hooks/useUserLimits";
import { ExamsProvider } from "../hooks/useExams";
import FileSidebar from "./FileSidebar";

interface CourseNotebookControllerProps {
  isSidebar?: boolean;
  courseId?: string;
  fileId?: string;
  returnUrl?: string;
}

export function CourseNotebookController(
  { isSidebar, courseId, fileId, returnUrl }: CourseNotebookControllerProps = {
    isSidebar: false,
  }
) {
  const { token, authUser } = useAuth();
  const { requestValidToken, sendMessageAndListen } = useCommunication();
  const [userLimits, setUserLimits] = useState<StudyUserLimits>();
  // for files sidebar
  const [file, setFile] = useState<StudyNotUploadedDocument | null>(null);
  useEffect(() => {
    if (!token) return;
    (async () => {
      client.setConfig({
        baseUrl: `${CLOUDFLARE_URL}`,
        auth: requestValidToken,
      });
      const { data, error } = await getStudyDocumentsLimits();
      if (error) {
        console.error(error);
        toast.error(
          "Failed to access BetterCampus servers. Try reloading the page!"
        );
      } else setUserLimits(data);
    })();
  }, [token]);

  const { courseData, visibleCourseIds } = useCourses();
  const [notebooks, setNotebooks] = useState<NotebookExtended[] | null>(null);

  useEffect(() => {
    if (!courseData || !authUser || !authUser.id) return;
    (async () => {
      const [{ data, error }] = await Promise.all([getStudyNotebooks()]);
      if (error) {
        console.error(error);
        // forceRefreshToken();
        // toast.error(
        //   "Failed to access BetterCampus servers. Try reloading the page!"
        // );
      } else {
        // filter notebooks to current courses
        // TODO (future): handle notebooks outside of current courses
        const courseNotebooks = data.notebooks.filter(
          (notebook) => notebook.course_id in courseData
        );
        setNotebooks(courseNotebooks);
      }
    })();
  }, [courseData, authUser]);

  const [assignments, setAssignments] = useState<FinalAssignment[] | null>(
    null
  );

  const getAssignments = async () => {
    // With response
    const result = await sendMessageAndListen({
      type: "CALL_EXTENSION_FUNC",
      payload: { func: "getUpcomingAssignments" },
    });

    if (!result.data || !result.success) return [];

    return result.data as FinalAssignment[];
  };

  useEffect(() => {
    (async () => {
      if (courseId && fileId) {
        const result = await sendMessageAndListen({
          type: "CALL_EXTENSION_FUNC",
          payload: {
            func: "getCourseFile",
            args: {
              courseId,
              fileId,
            },
          },
        });
        if (result.data && result.success) setFile(result.data);
      }
    })();
  }, [courseId, fileId]);

  useEffect(() => {
    (async () => {
      setAssignments(await getAssignments());
    })();
  }, []);

  const courses = visibleCourseIds.map((id) => ({
    ...courseData[id],
    id: id,
  }));

  const course = courseId ? courses.find((x) => x.id === courseId) : undefined;

  if (
    !notebooks ||
    !courses ||
    !courses.length ||
    !token ||
    !userLimits ||
    !assignments ||
    (isSidebar && !file && !returnUrl)
  )
    return <WorkspaceSkeleton />;

  const defaultNotebook = isSidebar
    ? {
        id: -1,
        name: "File Chat",
        user_id: "",
        course_id: "",
        school_id: null,
        notebook_documents: [
          {
            id: -1,
            notebook_id: 0,
            document_id: 0,
            name: "File",
            source: "course",
          },
        ],
        highlights: [],
      }
    : undefined;

  return (
    <UserLimitsProvider initialLimits={userLimits}>
      <NotebookProvider
        defaultNotebook={defaultNotebook}
        allNotebooks={
          defaultNotebook ? [...notebooks, defaultNotebook] : notebooks
        }
      >
        <ExamsProvider initialExams={assignments}>
          {isSidebar ? (
            <FileSidebar course={course} file={file} returnUrl={returnUrl} />
          ) : (
            <StudyLandingPage courses={courses} assignments={assignments} />
          )}
        </ExamsProvider>
      </NotebookProvider>
    </UserLimitsProvider>
  );
}
